/**
 * BeforeStateCarousel — compact carousel for the BEFORE state layout
 *
 * BEFORE: 201px tall, text col flex:1, image col 240px
 * AFTER:  177px tall, image col shrinks to 177px
 *
 * Manual navigation only — no auto-advance.
 * Starts on the Darwin hero slide. All 13 slides from ProductCarousel included.
 */

import { useState, useCallback, useRef } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { trackCarouselCtaClicked } from '../../lib/analytics';

// ── Light-mode palette (matches PrimateVisionV2 L) ──────────────────────────
const L = {
  bgSurface:   '#FAFAF7',
  border:      '#D4D0C8',
  textPrimary: '#1A1916',
  textMuted:   '#7A7669',
  signal:      '#1B4FE0',
};

// ── Slide data — all 13 slides ────────────────────────────────────────────────
const SLIDES: {
  eyebrow?: string;
  heading: string;
  body: string;
  cta?: { label: string; href: string };
  imgSrc: string;
  imgBg: string;   // match each image's own background so contain has no bars
}[] = [
  // 0 — Darwin hero (warm cream bg)
  {
    heading: 'Darwin watches your video and tells you what\'s happening — in real time.',
    body: 'Point your camera. Type what to look for. Darwin streams results as it watches.',
    imgSrc: '/images/slide-hero-darwin-watching.jpg',
    imgBg: '#FAFAF7',
  },
  // 1 — JEPA world models (dark/void bg)
  {
    eyebrow: 'Platform',
    heading: 'Real-time video intelligence for physical AI.',
    body: 'Built on JEPA world models — the first deployable JEPA perception product. Cloud performance. Edge costs.',
    // PRI-10: was /v2/docs (now stub) → link to JEPA explainer blog placeholder.
    cta: { label: 'How JEPA works →', href: 'https://www.primateintelligence.ai/blog/how-jepa-works' },
    imgSrc: '/images/slide0-brain-diagram.png',
    imgBg: '#FAFAF7',
  },
  // 2 — SOTA (dark bg)
  {
    eyebrow: 'Edge',
    heading: 'SOTA inference left the cloud.',
    body: 'Research-grade video benchmarks running on $400 hardware. Not a tradeoff.',
    imgSrc: '/images/slide1-sota.png',
    imgBg: '#FAFAF7',
  },
  // 3 — VLMs hallucinate (dark bg)
  {
    eyebrow: 'Accuracy',
    heading: "VLMs hallucinate. JEPA can't.",
    body: 'Deterministic — reasons from evidence, not pattern completion.',
    // PRI-10: was /v2/docs#benchmarks (now stub) → link to benchmarks blog placeholder.
    cta: { label: 'Benchmarks →', href: 'https://www.primateintelligence.ai/blog/benchmarks-deep-dive' },
    imgSrc: '/images/slide2-jepa-cant.png',
    imgBg: '#FAFAF7',
  },
  // 4 — 10× cheaper (dark bg)
  {
    eyebrow: 'Cost',
    heading: '10× cheaper. More accurate.',
    body: 'On-device inference with orders of magnitude more efficiency than VLMs.',
    cta: { label: 'See pricing →', href: '/v2/pricing' },
    imgSrc: '/images/slide3-10x-cheaper.png',
    imgBg: '#FAFAF7',
  },
  // 5 — Smart Cameras
  {
    eyebrow: 'Smart Cameras',
    heading: 'From detection to understanding. Same camera.',
    body: "Behavioural understanding, not bounding boxes. Primate Vision knows what someone is doing, not just that they're present.",
    imgSrc: '/images/slide4-smart-cameras.png',
    imgBg: '#FAFAF7',
  },
  // 6 — Robotics
  {
    eyebrow: 'Robotics',
    heading: 'World models that predict before robots act.',
    body: 'World model perception for robotic systems. Predict the interaction before the motor decision.',
    imgSrc: '/images/slide5-robotics.png',
    imgBg: '#FAFAF7',
  },
  // 7 — Healthcare
  {
    eyebrow: 'Healthcare',
    heading: "Hallucinations aren't a bug. They're a risk.",
    body: 'Every output is verifiable, repeatable and grounded in what it observed.',
    imgSrc: '/images/slide6-healthcare.png',
    imgBg: '#FAFAF7',
  },
  // 8 — Drones / UAV
  {
    eyebrow: 'Drones / UAV',
    heading: 'Real-time on-device inference. Finally.',
    body: '100% on-device real-time processing. No cloud roundtrips.',
    imgSrc: '/images/slide7-drones.png',
    imgBg: '#FAFAF7',
  },
  // 9 — Autonomous Vehicles
  {
    eyebrow: 'Autonomous Vehicles',
    heading: 'AV perception that anticipates.',
    body: "AV perception architectures excel at detecting what's there. The hardest problem is predicting what will happen next.",
    imgSrc: '/images/slide8-autonomous.png',
    imgBg: '#FAFAF7',
  },
  // 10 — Video Search
  {
    eyebrow: 'Video Search',
    heading: 'Deterministic video search at a fraction of VLM cost.',
    body: 'Query video in natural language. Find the frame where the event happened.',
    imgSrc: '/images/slide9-video-search.png',
    imgBg: '#FAFAF7',
  },
  // 11 — Video Ad Tech
  {
    eyebrow: 'Video Ad Tech',
    heading: 'Real-time scene understanding. Deterministic.',
    body: 'Contextual targeting that understands what\'s happening, not just what objects are present.',
    imgSrc: '/images/slide10-adtech.png',
    imgBg: '#FAFAF7',
  },
  // 12 — Benchmarks (using brain diagram as stand-in since no dedicated benchmark image)
  {
    eyebrow: 'Benchmarks',
    heading: "We didn't move the goalposts. We cleared them.",
    body: 'SSv2 top-1 and EK-100 results that match or exceed the largest VLMs at 10× lower cost.',
    // PRI-10: was /v2/docs#benchmarks (now stub) → link to benchmarks blog placeholder.
    cta: { label: 'See the numbers →', href: 'https://www.primateintelligence.ai/blog/benchmarks-deep-dive' },
    imgSrc: '/images/slide0-brain-diagram.png',
    imgBg: '#FAFAF7',
  },
];

// ── Component ────────────────────────────────────────────────────────────────
interface BeforeStateCarouselProps {
  afterState?: boolean;
}

export default function BeforeStateCarousel({ afterState = false }: BeforeStateCarouselProps) {
  const [active, setActive] = useState(0);
  const numSlides = SLIDES.length;
  const touchStartX = useRef(0);
  const isMobile = useIsMobile();
  // Narrow image col on mobile so the text column has enough room for 3-4 lines
  const imageColWidth = isMobile ? 130 : (afterState ? 177 : 240);

  const goTo = useCallback((idx: number) => setActive(idx), []);
  const prev = useCallback(() => goTo((active - 1 + numSlides) % numSlides), [active, numSlides, goTo]);
  const next = useCallback(() => goTo((active + 1) % numSlides), [active, numSlides, goTo]);

  const slide = SLIDES[active];

  return (
    <div style={{ width: '100%', flexShrink: 0 }}>
      {/* Card */}
      <div style={{
        width: '100%', position: 'relative',
        background: L.bgSurface,
        border: `1px solid ${L.border}`,
        borderRadius: 14,
        overflow: 'hidden',
        display: 'flex',
        height: afterState ? 177 : 201,
        transition: 'height 0.5s cubic-bezier(0.4,0,0.2,1)',
        // pan-y: browser handles vertical scroll; horizontal is owned by our swipe handler
        // This prevents the whole page from nudging sideways on carousel swipes.
        touchAction: 'pan-y',
      }}
      onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        const delta = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(delta) > 50) delta > 0 ? next() : prev();
      }}
    >

        {/* Prev nav */}
        <button onClick={prev} aria-label="Previous slide" style={{
          position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: 7, zIndex: 4,
          width: 26, height: 26, borderRadius: '50%',
          background: 'rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.09)',
          color: L.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
        }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6" /></svg>
        </button>

        {/* Text column — flex:1 fills from left nav button to image */}
        <div style={{
          flex: 1,
          minWidth: 0,
          padding: '16px 20px 16px 40px', /* left pad clears nav button */
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          borderRight: `1px solid ${L.border}`,
          background: L.bgSurface,
          overflow: 'hidden',
        }}>
          {slide.eyebrow && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start',
              padding: '2px 8px', borderRadius: 8, marginBottom: 6,
              fontSize: 8, fontWeight: 700,
              fontFamily: '"JetBrains Mono", monospace',
              letterSpacing: '0.14em', textTransform: 'uppercase',
              color: L.signal, background: 'rgba(32,88,240,0.08)',
              border: '1px solid rgba(32,88,240,0.2)',
            }}>
              {slide.eyebrow}
            </div>
          )}
          <div style={{
            color: L.textPrimary, fontSize: 13, fontWeight: 800,
            letterSpacing: '-0.018em', lineHeight: 1.25,
            marginBottom: 5,
            display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {slide.heading}
          </div>
          <div style={{
            color: L.textMuted, fontSize: 12, lineHeight: 1.45,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {slide.body}
          </div>
          {slide.cta && (
            <a
              href={slide.cta.href}
              onClick={() => trackCarouselCtaClicked({ slide_index: active, cta_label: slide.cta!.label, destination: slide.cta!.href })}
              style={{
                marginTop: 7,
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 10, fontWeight: 700,
                fontFamily: '"JetBrains Mono", monospace',
                letterSpacing: '0.08em', textTransform: 'uppercase',
                color: 'rgba(32,88,240,0.7)', textDecoration: 'none',
              }}
            >
              {slide.cta.label}
            </a>
          )}
        </div>

        {/* Image column — fixed width matching imgBg to eliminate letterbox bars */}
        <div style={{
          width: imageColWidth,
          flexShrink: 0,
          background: slide.imgBg,
          position: 'relative', overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'width 0.52s cubic-bezier(0.4,0,0.2,1)',
        }}>
          <img
            src={slide.imgSrc}
            alt={slide.heading}
            style={{
              width: '100%', height: '100%',
              objectFit: 'contain',   /* whole image visible — no clipping */
              display: 'block',
            }}
          />
        </div>

        {/* Next nav */}
        <button onClick={next} aria-label="Next slide" style={{
          position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: 7, zIndex: 4,
          width: 26, height: 26, borderRadius: '50%',
          background: 'rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.09)',
          color: L.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
        }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6" /></svg>
        </button>
      </div>

      {/* Dot indicators */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginTop: 7 }}>
        {SLIDES.map((_, i) => (
          <div
            key={i}
            onClick={() => goTo(i)}
            role="button"
            aria-label={`Go to slide ${i + 1}`}
            style={{
              height: 5, borderRadius: 3, cursor: 'pointer',
              width: i === active ? 14 : 5,
              background: i === active ? L.textPrimary : L.border,
              transition: 'width 200ms ease, background 200ms ease',
            }}
          />
        ))}
      </div>
    </div>
  );
}
